/**
 * Freeform geometry — the editable Bézier path behind Publisher's "Edit Shape ▸ Edit Points".
 *
 * A freeform lives in the same 100×100 viewBox space as SHAPE_GEOMETRY (stretched to the object box):
 *   freeform = {
 *     paths: [{ closed: boolean, nodes: [{ x, y, type: 'corner'|'smooth'|'straight', in: {x,y}|null, out: {x,y}|null }] }],
 *     evenodd?: boolean,   // fill rule (donut, frame…)
 *     noFill?: boolean,    // converted from an "open" preset (connectors, brackets) → no fill until a path is closed
 *   }
 * `in` / `out` are absolute control points of the segments entering / leaving the node (null = straight).
 */
import { SHAPE_GEOMETRY } from './shapes';

const KAPPA = 0.5522847498;
const r2 = (n) => Math.round(n * 100) / 100;
export const P = (x, y) => ({ x: r2(x), y: r2(y) });
export const mkNode = (x, y, type = 'corner', cin = null, cout = null) => ({ x: r2(x), y: r2(y), type, in: cin ? P(cin.x, cin.y) : null, out: cout ? P(cout.x, cout.y) : null });

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

/* ------------------------------------------------------------------ */
/* SVG path parsing → cubic segments                                    */
/* ------------------------------------------------------------------ */

const tokenize = (d) => d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) || [];

/** Arc (endpoint parameterisation) → list of cubic segments [{ c1, c2, p }]. */
const arcToCubics = (x1, y1, rx, ry, phiDeg, fa, fs, x2, y2) => {
  if (x1 === x2 && y1 === y2) return [];
  if (!rx || !ry) return [{ c1: null, c2: null, p: { x: x2, y: y2 } }];
  rx = Math.abs(rx); ry = Math.abs(ry);
  const phi = (phiDeg * Math.PI) / 180, cp = Math.cos(phi), sp = Math.sin(phi);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p = cp * dx + sp * dy, y1p = -sp * dx + cp * dy;
  const lam = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lam > 1) { const s = Math.sqrt(lam); rx *= s; ry *= s; }
  const sign = fa === fs ? -1 : 1;
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const coef = sign * Math.sqrt(Math.max(0, num / den));
  const cxp = coef * ((rx * y1p) / ry), cyp = coef * (-(ry * x1p) / rx);
  const cx = cp * cxp - sp * cyp + (x1 + x2) / 2, cy = sp * cxp + cp * cyp + (y1 + y2) / 2;
  const ang = (ux, uy, vx, vy) => { const a = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy); return a; };
  const th1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dth = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!fs && dth > 0) dth -= 2 * Math.PI;
  else if (fs && dth < 0) dth += 2 * Math.PI;
  const segs = Math.max(1, Math.ceil(Math.abs(dth) / (Math.PI / 2) - 1e-9));
  const delta = dth / segs;
  const t = (4 / 3) * Math.tan(delta / 4);
  const out = [];
  let th = th1;
  const point = (a) => ({ x: cx + rx * Math.cos(a) * cp - ry * Math.sin(a) * sp, y: cy + rx * Math.cos(a) * sp + ry * Math.sin(a) * cp });
  const deriv = (a) => ({ x: -rx * Math.sin(a) * cp - ry * Math.cos(a) * sp, y: -rx * Math.sin(a) * sp + ry * Math.cos(a) * cp });
  for (let i = 0; i < segs; i++) {
    const a0 = th, a1 = th + delta;
    const p0 = point(a0), p1 = point(a1), d0 = deriv(a0), d1 = deriv(a1);
    out.push({ c1: { x: p0.x + t * d0.x, y: p0.y + t * d0.y }, c2: { x: p1.x - t * d1.x, y: p1.y - t * d1.y }, p: i === segs - 1 ? { x: x2, y: y2 } : p1 });
    th = a1;
  }
  return out;
};

/** Parse an SVG path into subpaths of cubic/line segments: [{ start, segs: [{ c1, c2, p }], closed }]. */
export const parsePath = (d) => {
  const tk = tokenize(d || '');
  const subs = [];
  let cur = null, cmd = null, i = 0;
  let x = 0, y = 0, sx = 0, sy = 0, lastC = null, lastQ = null;
  const num = () => parseFloat(tk[i++]);
  const begin = (nx, ny) => { cur = { start: { x: nx, y: ny }, segs: [], closed: false }; subs.push(cur); sx = nx; sy = ny; x = nx; y = ny; };
  const seg = (c1, c2, px, py) => { if (!cur) begin(x, y); cur.segs.push({ c1, c2, p: { x: px, y: py } }); x = px; y = py; };
  while (i < tk.length) {
    if (/[a-zA-Z]/.test(tk[i])) cmd = tk[i++];
    else if (cmd === 'M') cmd = 'L'; else if (cmd === 'm') cmd = 'l';
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    let prevC = null, prevQ = null;
    switch (C) {
      case 'M': { const nx = num(), ny = num(); begin(rel ? x + nx : nx, rel ? y + ny : ny); break; }
      case 'L': { const nx = num(), ny = num(); seg(null, null, rel ? x + nx : nx, rel ? y + ny : ny); break; }
      case 'H': { const nx = num(); seg(null, null, rel ? x + nx : nx, y); break; }
      case 'V': { const ny = num(); seg(null, null, x, rel ? y + ny : ny); break; }
      case 'C': {
        const a = num(), b = num(), c = num(), dd = num(), e = num(), f = num();
        const c1 = { x: rel ? x + a : a, y: rel ? y + b : b }, c2 = { x: rel ? x + c : c, y: rel ? y + dd : dd };
        seg(c1, c2, rel ? x + e : e, rel ? y + f : f); prevC = c2; break;
      }
      case 'S': {
        const c = num(), dd = num(), e = num(), f = num();
        const c1 = lastC ? { x: 2 * x - lastC.x, y: 2 * y - lastC.y } : { x, y };
        const c2 = { x: rel ? x + c : c, y: rel ? y + dd : dd };
        seg(c1, c2, rel ? x + e : e, rel ? y + f : f); prevC = c2; break;
      }
      case 'Q': {
        const a = num(), b = num(), e = num(), f = num();
        const q = { x: rel ? x + a : a, y: rel ? y + b : b }, p = { x: rel ? x + e : e, y: rel ? y + f : f };
        seg({ x: x + (2 / 3) * (q.x - x), y: y + (2 / 3) * (q.y - y) }, { x: p.x + (2 / 3) * (q.x - p.x), y: p.y + (2 / 3) * (q.y - p.y) }, p.x, p.y); prevQ = q; break;
      }
      case 'T': {
        const e = num(), f = num();
        const q = lastQ ? { x: 2 * x - lastQ.x, y: 2 * y - lastQ.y } : { x, y };
        const p = { x: rel ? x + e : e, y: rel ? y + f : f };
        seg({ x: x + (2 / 3) * (q.x - x), y: y + (2 / 3) * (q.y - y) }, { x: p.x + (2 / 3) * (q.x - p.x), y: p.y + (2 / 3) * (q.y - p.y) }, p.x, p.y); prevQ = q; break;
      }
      case 'A': {
        const rx = num(), ry = num(), rot = num(), fa = num(), fs = num(), e = num(), f = num();
        const ex = rel ? x + e : e, ey = rel ? y + f : f;
        for (const s of arcToCubics(x, y, rx, ry, rot, fa, fs, ex, ey)) seg(s.c1, s.c2, s.p.x, s.p.y);
        break;
      }
      case 'Z': { if (cur) { cur.closed = true; x = sx; y = sy; cur = null; } break; }
      default: i++; break;
    }
    lastC = prevC; lastQ = prevQ;
  }
  return subs.filter((s) => s.segs.length || s.closed);
};

/* ------------------------------------------------------------------ */
/* Node model                                                           */
/* ------------------------------------------------------------------ */

/** Infer Publisher point type from the two handles. */
export const inferType = (n) => {
  if (!n.in || !n.out) return 'corner';
  const a = { x: n.in.x - n.x, y: n.in.y - n.y }, b = { x: n.out.x - n.x, y: n.out.y - n.y };
  const la = Math.hypot(a.x, a.y), lb = Math.hypot(b.x, b.y);
  if (la < 1e-6 || lb < 1e-6) return 'corner';
  const cross = Math.abs(a.x * b.y - a.y * b.x) / (la * lb);
  const dot = (a.x * b.x + a.y * b.y) / (la * lb);
  if (cross > 0.03 || dot > -0.97) return 'corner';
  return Math.abs(la - lb) < 0.5 ? 'smooth' : 'straight';
};

/** Cubic subpaths → node paths (merging a closing point that coincides with the start). */
const subpathsToPaths = (subs) => subs.map((s) => {
  const nodes = [mkNode(s.start.x, s.start.y)];
  for (const sg of s.segs) {
    const prev = nodes[nodes.length - 1];
    if (sg.c1) prev.out = P(sg.c1.x, sg.c1.y);
    nodes.push(mkNode(sg.p.x, sg.p.y, 'corner', sg.c2 ? sg.c2 : null, null));
  }
  if (s.closed && nodes.length > 1 && dist(nodes[0], nodes[nodes.length - 1]) < 0.05) {
    const last = nodes.pop();
    nodes[0].in = last.in;
  }
  nodes.forEach((n) => { n.type = inferType(n); });
  return { closed: !!s.closed, nodes };
}).filter((p) => p.nodes.length >= 2);

/* ------------------------------------------------------------------ */
/* Preset shape → freeform                                              */
/* ------------------------------------------------------------------ */

const rectPath = () => ({ closed: true, nodes: [mkNode(0, 0), mkNode(100, 0), mkNode(100, 100), mkNode(0, 100)] });

const roundedRectPath = (rx, ry) => {
  rx = Math.max(0, Math.min(50, rx)); ry = Math.max(0, Math.min(50, ry));
  if (rx < 0.01 || ry < 0.01) return rectPath();
  const kx = rx * KAPPA, ky = ry * KAPPA;
  const n = [];
  // clockwise from the top-left corner's end of arc
  n.push(mkNode(rx, 0, 'corner', { x: rx - kx, y: 0 }, null));
  n.push(mkNode(100 - rx, 0, 'corner', null, { x: 100 - rx + kx, y: 0 }));
  n.push(mkNode(100, ry, 'corner', { x: 100, y: ry - ky }, null));
  n.push(mkNode(100, 100 - ry, 'corner', null, { x: 100, y: 100 - ry + ky }));
  n.push(mkNode(100 - rx, 100, 'corner', { x: 100 - rx + kx, y: 100 }, null));
  n.push(mkNode(rx, 100, 'corner', null, { x: rx - kx, y: 100 }));
  n.push(mkNode(0, 100 - ry, 'corner', { x: 0, y: 100 - ry + ky }, null));
  n.push(mkNode(0, ry, 'corner', null, { x: 0, y: ry - ky }));
  return { closed: true, nodes: n };
};

const ellipsePath = () => {
  const k = 50 * KAPPA;
  return { closed: true, nodes: [
    mkNode(50, 0, 'smooth', { x: 50 - k, y: 0 }, { x: 50 + k, y: 0 }),
    mkNode(100, 50, 'smooth', { x: 100, y: 50 - k }, { x: 100, y: 50 + k }),
    mkNode(50, 100, 'smooth', { x: 50 + k, y: 100 }, { x: 50 - k, y: 100 }),
    mkNode(0, 50, 'smooth', { x: 0, y: 50 + k }, { x: 0, y: 50 - k }),
  ] };
};

export const isFreeform = (o) => !!o && o.type === 'shape' && o.shapeType === 'freeform' && Array.isArray(o.properties?.freeform?.paths);

/**
 * Convert a preset shape object into a freeform (bakes flips, keeps arrows / fill rule).
 * Returns { shapeType, properties } patches, or null when the object already is a freeform.
 */
export const toFreeform = (o) => {
  if (!o || o.type !== 'shape') return null;
  if (isFreeform(o)) return null;
  const geom = SHAPE_GEOMETRY[o.shapeType] || SHAPE_GEOMETRY.rectangle;
  const p = o.properties || {};
  let paths = [];
  const ff = { paths, evenodd: !!geom.evenodd, noFill: !!geom.open || geom.kind === 'line' };
  if (geom.kind === 'rect') {
    const w = Math.max(1, o.width), h = Math.max(1, o.height);
    const r = geom.rounded ? Math.max(p.cornerRadius || 0, Math.min(w, h) * 0.15) : (p.cornerRadius || 0);
    paths.push(r > 0 ? roundedRectPath((r / w) * 100, (r / h) * 100) : rectPath());
  } else if (geom.kind === 'ellipse') paths.push(ellipsePath());
  else if (geom.kind === 'polygon' && geom.points) {
    const nodes = geom.points.trim().split(/\s+/).map((pair) => pair.split(',').map(Number)).filter((q) => q.length === 2 && q.every(Number.isFinite)).map(([x, y]) => mkNode(x, y));
    paths.push(nodes.length >= 3 ? { closed: true, nodes } : rectPath());
  } else if (geom.kind === 'line') {
    paths.push({ closed: false, nodes: [mkNode(0, 50), mkNode(100, 50)] });
  } else if (geom.kind === 'path' && geom.d) {
    paths.push(...subpathsToPaths(parsePath(geom.d)));
    if (!paths.length) paths.push(rectPath());
  } else paths.push(rectPath());
  // bake flips so the overlay and the renderer agree
  if (p.flipX || p.flipY) {
    const fl = (q) => (q ? P(p.flipX ? 100 - q.x : q.x, p.flipY ? 100 - q.y : q.y) : null);
    for (const path of paths) for (const n of path.nodes) { const q = fl(n); n.x = q.x; n.y = q.y; n.in = fl(n.in); n.out = fl(n.out); }
  }
  const props = { ...p, freeform: ff, flipX: false, flipY: false, cornerRadius: 0 };
  if (geom.arrowEnd) props.arrowEnd = true;
  if (geom.arrowStart) props.arrowStart = true;
  return { shapeType: 'freeform', properties: props };
};

/* ------------------------------------------------------------------ */
/* Serialisation                                                        */
/* ------------------------------------------------------------------ */

const segD = (a, b) => (a.out || b.in ? `C${r2((a.out || a).x)},${r2((a.out || a).y)} ${r2((b.in || b).x)},${r2((b.in || b).y)} ${r2(b.x)},${r2(b.y)}` : `L${r2(b.x)},${r2(b.y)}`);

export const pathToD = (path) => {
  const n = path.nodes;
  if (!n.length) return '';
  let d = `M${r2(n[0].x)},${r2(n[0].y)}`;
  for (let i = 1; i < n.length; i++) d += ` ${segD(n[i - 1], n[i])}`;
  if (path.closed && n.length > 1) d += ` ${segD(n[n.length - 1], n[0])} Z`;
  return d;
};

/** { all, closed } path strings: fill uses `closed`, stroke uses `all`. */
export const freeformToD = (ff) => {
  const paths = ff?.paths || [];
  return { all: paths.map(pathToD).join(' '), closed: paths.filter((p) => p.closed).map(pathToD).join(' ') };
};

/* ------------------------------------------------------------------ */
/* Segment math                                                         */
/* ------------------------------------------------------------------ */

/** Segment i of a path goes from node i to node (i+1) (wrapping for closed paths). */
export const segmentCount = (path) => (path.closed ? path.nodes.length : Math.max(0, path.nodes.length - 1));
export const segmentEnds = (path, i) => [path.nodes[i], path.nodes[(i + 1) % path.nodes.length]];
export const isCurved = (a, b) => !!(a.out || b.in);

export const evalSegment = (a, b, t) => {
  if (!isCurved(a, b)) return lerp(a, b, t);
  const c1 = a.out || a, c2 = b.in || b, mt = 1 - t;
  return {
    x: mt * mt * mt * a.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * b.x,
    y: mt * mt * mt * a.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * b.y,
  };
};

/** Closest parameter on segment a→b to point q (coarse sampling + refinement). */
export const closestT = (a, b, q) => {
  if (!isCurved(a, b)) {
    const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy || 1e-9;
    return Math.max(0, Math.min(1, ((q.x - a.x) * dx + (q.y - a.y) * dy) / l2));
  }
  let best = 0, bd = Infinity;
  for (let i = 0; i <= 40; i++) { const t = i / 40, d = dist(evalSegment(a, b, t), q); if (d < bd) { bd = d; best = t; } }
  let step = 1 / 80;
  for (let k = 0; k < 12; k++) {
    for (const t of [best - step, best + step]) { if (t < 0 || t > 1) continue; const d = dist(evalSegment(a, b, t), q); if (d < bd) { bd = d; best = t; } }
    step /= 2;
  }
  return best;
};

/** Sample a path into a polyline (viewBox space). */
export const samplePath = (path, perSeg = 10) => {
  const out = [];
  const n = segmentCount(path);
  if (!path.nodes.length) return out;
  if (n === 0) return [{ x: path.nodes[0].x, y: path.nodes[0].y }];
  for (let i = 0; i < n; i++) {
    const [a, b] = segmentEnds(path, i);
    if (!isCurved(a, b)) out.push({ x: a.x, y: a.y });
    else for (let k = 0; k < perSeg; k++) out.push(evalSegment(a, b, k / perSeg));
  }
  if (!path.closed) out.push({ x: path.nodes[path.nodes.length - 1].x, y: path.nodes[path.nodes.length - 1].y });
  return out;
};

/** Outline polygon for text wrapping (unit space): the largest closed path, else every path's samples. */
export const freeformOutline = (ff) => {
  const paths = ff?.paths || [];
  const closed = paths.filter((p) => p.closed && p.nodes.length >= 3);
  let pick = null, best = -1;
  for (const p of closed) {
    const s = samplePath(p, 6);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const q of s) { if (q.x < x0) x0 = q.x; if (q.x > x1) x1 = q.x; if (q.y < y0) y0 = q.y; if (q.y > y1) y1 = q.y; }
    const area = (x1 - x0) * (y1 - y0);
    if (area > best) { best = area; pick = s; }
  }
  const pts = pick || paths.flatMap((p) => samplePath(p, 6));
  return pts.length >= 3 ? pts.map((q) => [q.x / 100, q.y / 100]) : [[0, 0], [1, 0], [1, 1], [0, 1]];
};

/* ------------------------------------------------------------------ */
/* Edits (all return a new freeform)                                    */
/* ------------------------------------------------------------------ */

const clone = (ff) => JSON.parse(JSON.stringify(ff));

export const moveNode = (ff, pi, ni, dx, dy) => {
  const next = clone(ff);
  const n = next.paths[pi].nodes[ni];
  n.x = r2(n.x + dx); n.y = r2(n.y + dy);
  if (n.in) n.in = P(n.in.x + dx, n.in.y + dy);
  if (n.out) n.out = P(n.out.x + dx, n.out.y + dy);
  return next;
};

/** Default handle positions for a node without controls: a third of the way along the neighbouring segments. */
export const defaultHandles = (path, ni) => {
  const n = path.nodes[ni], len = path.nodes.length;
  const prev = path.closed || ni > 0 ? path.nodes[(ni - 1 + len) % len] : null;
  const nxt = path.closed || ni < len - 1 ? path.nodes[(ni + 1) % len] : null;
  const cin = n.in || (prev ? P(n.x + (prev.x - n.x) / 3, n.y + (prev.y - n.y) / 3) : null);
  const cout = n.out || (nxt ? P(n.x + (nxt.x - n.x) / 3, n.y + (nxt.y - n.y) / 3) : null);
  return { in: cin, out: cout };
};

/** Move one control handle; the opposite one follows the node type (smooth = mirror, straight = same line). */
export const setHandle = (ff, pi, ni, which, pos) => {
  const next = clone(ff);
  const path = next.paths[pi], n = path.nodes[ni];
  const other = which === 'in' ? 'out' : 'in';
  n[which] = P(pos.x, pos.y);
  if (n.type === 'smooth' || n.type === 'straight') {
    const v = { x: n[which].x - n.x, y: n[which].y - n.y };
    const lv = Math.hypot(v.x, v.y);
    if (lv > 1e-6) {
      const cur = n[other] || defaultHandles(path, ni)[other];
      const lo = n.type === 'smooth' ? lv : cur ? Math.hypot(cur.x - n.x, cur.y - n.y) : lv;
      n[other] = P(n.x - (v.x / lv) * lo, n.y - (v.y / lv) * lo);
    }
  }
  return next;
};

export const setNodeType = (ff, pi, ni, type) => {
  const next = clone(ff);
  const path = next.paths[pi], n = path.nodes[ni];
  n.type = type;
  if (type === 'corner') return next;
  const h = defaultHandles(path, ni);
  const cin = h.in, cout = h.out;
  if (!cin || !cout) { n.in = cin; n.out = cout; return next; }
  // average the two directions so the tangent passes through the node
  const a = { x: cin.x - n.x, y: cin.y - n.y }, b = { x: cout.x - n.x, y: cout.y - n.y };
  const la = Math.hypot(a.x, a.y) || 1, lb = Math.hypot(b.x, b.y) || 1;
  let dir = { x: a.x / la - b.x / lb, y: a.y / la - b.y / lb };
  let ld = Math.hypot(dir.x, dir.y);
  if (ld < 1e-6) { dir = { x: -b.y / lb, y: b.x / lb }; ld = 1; }
  dir = { x: dir.x / ld, y: dir.y / ld };
  const lin = type === 'smooth' ? (la + lb) / 2 : la, lout = type === 'smooth' ? (la + lb) / 2 : lb;
  n.in = P(n.x + dir.x * lin, n.y + dir.y * lin);
  n.out = P(n.x - dir.x * lout, n.y - dir.y * lout);
  return next;
};

/** Insert a node on segment `si` at parameter t (de Casteljau for curves). Returns { ff, index }. */
export const insertNode = (ff, pi, si, t) => {
  const next = clone(ff);
  const path = next.paths[pi];
  const a = path.nodes[si], bi = (si + 1) % path.nodes.length, b = path.nodes[bi];
  let created;
  if (!isCurved(a, b)) created = mkNode(lerp(a, b, t).x, lerp(a, b, t).y, 'corner');
  else {
    const p0 = a, p1 = a.out || a, p2 = b.in || b, p3 = b;
    const p01 = lerp(p0, p1, t), p12 = lerp(p1, p2, t), p23 = lerp(p2, p3, t);
    const p012 = lerp(p01, p12, t), p123 = lerp(p12, p23, t), pm = lerp(p012, p123, t);
    a.out = P(p01.x, p01.y); b.in = P(p23.x, p23.y);
    created = mkNode(pm.x, pm.y, 'straight', p012, p123);
    created.type = inferType(created) === 'corner' ? 'straight' : inferType(created);
  }
  path.nodes.splice(si + 1, 0, created);
  return { ff: next, index: si + 1 };
};

export const minNodes = (path) => (path.closed ? 3 : 2);

export const deleteNode = (ff, pi, ni) => {
  const path = ff.paths[pi];
  if (path.nodes.length <= minNodes(path)) return ff;
  const next = clone(ff);
  next.paths[pi].nodes.splice(ni, 1);
  return next;
};

export const setSegmentStraight = (ff, pi, si) => {
  const next = clone(ff);
  const path = next.paths[pi];
  const a = path.nodes[si], b = path.nodes[(si + 1) % path.nodes.length];
  a.out = null; b.in = null;
  if (a.type !== 'corner') a.type = 'corner';
  if (b.type !== 'corner') b.type = 'corner';
  return next;
};

export const setSegmentCurved = (ff, pi, si) => {
  const next = clone(ff);
  const path = next.paths[pi];
  const a = path.nodes[si], b = path.nodes[(si + 1) % path.nodes.length];
  if (!a.out) a.out = P(a.x + (b.x - a.x) / 3, a.y + (b.y - a.y) / 3);
  if (!b.in) b.in = P(b.x + (a.x - b.x) / 3, b.y + (a.y - b.y) / 3);
  return next;
};

export const setPathClosed = (ff, pi, closed) => {
  const next = clone(ff);
  next.paths[pi].closed = !!closed;
  if (closed) next.noFill = false;
  return next;
};

export const nodeCount = (ff) => (ff?.paths || []).reduce((a, p) => a + p.nodes.length, 0);

/** Mirror the geometry inside its frame (Flip Horizontal / Vertical are baked so the point editor stays in sync). */
export const flipFreeform = (ff, fx, fy) => {
  const next = clone(ff);
  const fl = (q) => (q ? P(fx ? 100 - q.x : q.x, fy ? 100 - q.y : q.y) : null);
  for (const p of next.paths) for (const n of p.nodes) { const q = fl(n); n.x = q.x; n.y = q.y; n.in = fl(n.in); n.out = fl(n.out); }
  return next;
};

/** Closest segment to a viewBox point: { pi, si, t, dist } (or null when the freeform has no segments). */
export const nearestSegment = (ff, q) => {
  let best = null;
  (ff?.paths || []).forEach((path, pi) => {
    for (let si = 0; si < segmentCount(path); si++) {
      const [a, b] = segmentEnds(path, si);
      const t = closestT(a, b, q);
      const d = dist(evalSegment(a, b, t), q);
      if (!best || d < best.dist) best = { pi, si, t, dist: d };
    }
  });
  return best;
};

/* ------------------------------------------------------------------ */
/* Bounds normalisation                                                 */
/* ------------------------------------------------------------------ */

/** Geometry bounds (viewBox space) using sampled curves. */
export const freeformBounds = (ff) => {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of ff.paths) for (const q of samplePath(p, 12)) { if (q.x < x0) x0 = q.x; if (q.x > x1) x1 = q.x; if (q.y < y0) y0 = q.y; if (q.y > y1) y1 = q.y; }
  return Number.isFinite(x0) ? { x0, y0, x1, y1 } : { x0: 0, y0: 0, x1: 100, y1: 100 };
};

/**
 * Publisher keeps the selection frame hugging the geometry. Given an object and its (possibly out-of-box) freeform,
 * return { x, y, width, height, freeform } with the geometry re-normalised to 0..100 — or null when already snug.
 * Rotation is respected (the frame moves along the object's local axes).
 */
export const normalizeFreeform = (o, ff) => {
  const b = freeformBounds(ff);
  const w = Math.max(1, o.width), h = Math.max(1, o.height);
  const spanX = b.x1 - b.x0, spanY = b.y1 - b.y0;
  const needX = spanX > 1e-6 && (Math.abs(b.x0) > 0.01 || Math.abs(b.x1 - 100) > 0.01);
  const needY = spanY > 1e-6 && (Math.abs(b.y0) > 0.01 || Math.abs(b.y1 - 100) > 0.01);
  if (!needX && !needY) return null;
  const nx0 = needX ? b.x0 : 0, nx1 = needX ? b.x1 : 100, ny0 = needY ? b.y0 : 0, ny1 = needY ? b.y1 : 100;
  const newW = Math.max(1, (w * (nx1 - nx0)) / 100), newH = Math.max(1, (h * (ny1 - ny0)) / 100);
  // local (unrotated) offset of the new centre relative to the old centre, in px
  const dxl = (((nx0 + nx1) / 2 - 50) / 100) * w, dyl = (((ny0 + ny1) / 2 - 50) / 100) * h;
  const r = ((o.rotation || 0) * Math.PI) / 180, cr = Math.cos(r), sr = Math.sin(r);
  const cx = o.x + w / 2 + dxl * cr - dyl * sr, cy = o.y + h / 2 + dxl * sr + dyl * cr;
  const sx = 100 / (nx1 - nx0), sy = 100 / (ny1 - ny0);
  const map = (q) => (q ? P((q.x - nx0) * sx, (q.y - ny0) * sy) : null);
  const next = clone(ff);
  for (const p of next.paths) for (const n of p.nodes) { const q = map(n); n.x = q.x; n.y = q.y; n.in = map(n.in); n.out = map(n.out); }
  return { x: cx - newW / 2, y: cy - newH / 2, width: newW, height: newH, freeform: next };
};
